// Web adapter for @kinde-oss/react-native-sdk-0-7x
// Implements the same interface using browser-based PKCE OAuth flow

const TOKEN_KEY = 'kinde_access_token';
const REFRESH_KEY = 'kinde_refresh_token';

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// Treat the token as expired a bit before its real exp, so we refresh
// proactively instead of letting it expire mid-request. This tenant issues
// very short-lived access tokens (~60s), so without a buffer a request can be
// sent with a token that expires in-flight → 401 (surfaced as a CORS error on
// web, since the error response lacks CORS headers). The buffer is capped at a
// fraction of the token's own lifetime so it still works for longer-lived tokens.
const EXPIRY_BUFFER_MS = 20_000;

function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const lifetimeMs = (payload.exp - (payload.iat ?? payload.exp)) * 1000;
    const buffer = Math.min(EXPIRY_BUFFER_MS, lifetimeMs / 2);
    return payload.exp * 1000 - buffer <= Date.now();
  } catch {
    return true;
  }
}

// Exchange the stored refresh_token for a fresh access_token. Deduplicated so
// concurrent callers (e.g. several API requests firing at once) share one
// network round-trip. This mirrors what the native Kinde SDK does internally.
let refreshInFlight = null;

async function doRefresh() {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) return null;

  const issuerUrl = process.env.KINDE_ISSUER_URL;
  const clientId = process.env.KINDE_CLIENT_ID;
  if (!issuerUrl || !clientId) return null;

  try {
    const response = await fetch(`${issuerUrl}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshToken,
      }).toString(),
    });
    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
      if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
      return data;
    }
    return null;
  } catch {
    return null;
  }
}

function refreshTokens() {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// Returns a non-expired access token, transparently refreshing if the stored
// one is missing/expired. The request interceptor relies on this so outgoing
// requests always carry a valid token (matching native behaviour) instead of
// sending a stale token and depending on a 401 round-trip to recover.
async function getValidAccessToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !isTokenExpired(token)) return token;
  const data = await refreshTokens();
  return data?.access_token ?? null;
}

export class KindeSDK {
  constructor(issuerUrl, callbackUrl, clientId, logoutUrl) {
    this.issuerUrl = issuerUrl;
    this.callbackUrl = callbackUrl;
    this.clientId = clientId;
    this.logoutUrl = logoutUrl;
  }

  get isAuthenticated() {
    // Refresh-aware: an expired access token with a valid refresh token still
    // counts as authenticated, so startup doesn't force a full re-login.
    return getValidAccessToken().then((t) => !!t);
  }

  async login() {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem('kinde_pkce_verifier', verifier);

    // Kinde requires an OAuth `state` (>= 8 chars) for CSRF protection.
    const state = generateCodeVerifier();
    sessionStorage.setItem('kinde_oauth_state', state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.callbackUrl,
      scope: 'openid profile email offline',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
    });

    window.location.href = `${this.issuerUrl}/oauth2/auth?${params.toString()}`;
    return new Promise(() => {}); // browser navigates away
  }

  async logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    const params = new URLSearchParams({ redirect: this.logoutUrl });
    window.location.href = `${this.issuerUrl}/logout?${params.toString()}`;
  }

  async getUserDetails() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return {};
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        id: payload.sub || '',
        email: payload.email || '',
        given_name: payload.given_name || '',
        family_name: payload.family_name || '',
        picture: payload.picture || '',
      };
    } catch {
      return {};
    }
  }

  async forceTokenRefresh() {
    return (await refreshTokens()) ?? undefined;
  }

  async getPermissions() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return { permissions: [], orgCode: '' };
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        permissions: payload.permissions || [],
        orgCode: payload.org_code || '',
      };
    } catch {
      return { permissions: [], orgCode: '' };
    }
  }

  async cleanUp() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem('kinde_pkce_verifier');
  }
}

// Matches the Storage export used in AuthService.
// getAccessToken refreshes transparently when the stored token is expired, so
// the request interceptor always attaches a valid token (like the native SDK).
export const Storage = {
  getAccessToken: () => getValidAccessToken(),
  setAccessToken: (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    return Promise.resolve();
  },
  getRefreshToken: () => Promise.resolve(localStorage.getItem(REFRESH_KEY)),
  setRefreshToken: (token) => {
    localStorage.setItem(REFRESH_KEY, token);
    return Promise.resolve();
  },
  clearAll: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    return Promise.resolve();
  },
};
